import { getUniqueValues } from '../../../../components/miscUtils';
import { TpDataContext, VALTYPES, VALTYPE_ANY } from '../../../../data/interfaces';
import {
  CONFIGPARAMTYPES,
  createConfigParamMultipleChoice,
  createConfigParamTypeDiscreteColorScheme,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpChartConfigParams, TpChartDataBinding, getBoundData } from '../chartDataBindings';
import { CONFIGPARAM_VISIBILITYCONDITION_TYPES, TpChartManifest } from '../interfaces';
import { StackedCompositionPlot } from './StackedCompositionPlot';
import { createCategoricalOrderConfigParam } from '../../helpers/colorMappingInfo';

export const manifestStackedCompositionPlot: TpChartManifest = {
  id: 'StackedCompositionPlot',
  name: 'Stacked composition plot',
  description: `Creates one stacked bar for each selected numerical property in a data table. Each bar represents one entity from a linked table. Within a bar, rows from the primary table are grouped by the categorical \`compositionGroups\` property, and their numerical values are summed within each group. Absolute stacking shows these sums; relative stacking normalizes each bar to its total.

Data setup: the plot uses two tables.
- The primary table, selected by \`dataBinding.dataTableId\`, contains the composition items as rows and the numerical matrix properties as columns. Bind \`compositionGroups\` to a categorical property in this table. Each matrix property must be numeric and have a \`groupId\` matching \`columnsGroupId\`, plus **each numerical property must have the following extra keys in the table manifest (!)**:
  - \`columnCanonicalTypeId\`: the canonical type of the linked entities represented by the plot columns.
  - \`columnItemId\`: the ID of the linked entity represented by this property. It must match the entity's item ID (the linked table's \`colItemId\` values); it is not the matrix property's own \`id\`.
- The linked table contains the entities represented by the matrix properties. Set \`dataBinding.columnsCanonicalTypeId\` to its canonical type ID and bind \`columnLabelId\`, \`sortColumnBy\`, and \`hoverLabelColumn\` to properties in this table. \`sortColumnBy\` is optional; when omitted, columns are sorted by total composition.

\`columnsGroupId\` selects matrix properties by their \`groupId\`; \`columnsCanonicalTypeId\` selects the linked entity type. A matrix property is included only when its \`columnCanonicalTypeId\` and \`groupId\` match both binding values, and its \`columnItemId\` resolves to an item in the linked table.

Example: metagenomics abundances by sample and taxon.
- The primary taxa table has one row per taxon, identified by its item ID (for example, \`taxon_id\`). It contains a categorical property such as \`family\`, which can be bound to \`compositionGroups\`.
- Store the abundances in wide format: one numeric property/column per sample, with each row holding that taxon's abundance for that sample. Put these properties in one property group, for example \`samples\`. For every abundance property, set \`groupId: samples\`, \`columnCanonicalTypeId\` to the samples table's canonical type (for example, \`sample\`), and \`columnItemId\` to the corresponding sample's item ID (for example, \`sample_01\`). The property ID can be different from the sample ID.
- The linked samples table has one row per sample, with a unique item ID such as \`sample_01\`, and may contain sample metadata such as treatment, site, or collection date. Bind its display-name property to \`columnLabelId\` and a suitable metadata property to \`hoverLabelColumn\`.
- Bind the chart with \`columnsGroupId: samples\` and \`columnsCanonicalTypeId: sample\`. The plot selects the abundance properties in that group, matches each to its sample through \`columnItemId\`, and labels each bar from the linked samples table.
`,
  showDataMatrix: true,
  dataMatrixDocumentationGroup: 'columns',
  component: StackedCompositionPlot,
  canvasBased: true,
  groups: [
    { groupId: 'columns', groupName: 'Columns' },
    { groupId: 'advanced', groupName: 'Advanced' },
  ],
  aspects: [
    {
      id: 'compositionGroups',
      groupId: 'columns',
      description: 'Each state of this categorical property is a different bar in the set of stacked bars.',
      name: 'Composition Groups',
      dataType: VALTYPES.CATEGORICAL,
      required: true,
    },

    {
      id: 'columnLabelId',
      name: 'Column labels',
      groupId: 'columns',
      description: 'Contains the displayed labels for the columns of the plot (on secondary table).',
      required: true,
      onColumnItems: true,
      dataType: [VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'sortColumnBy',
      name: 'Sort columns by',
      groupId: 'columns',
      description: 'Used to sort the columns of the plot (on secondary table).',
      onColumnItems: true,
      customAbsentLabel: 'Total composition',
      dataType: [VALTYPES.DENDROGRAM, VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'hoverLabelColumn',
      name: 'Hover label (columns)',
      groupId: 'columns',
      description: 'Text to be displayed when the mouse hovers over a column (on secondary table).',
      onColumnItems: true,
      required: true,
      dataType: VALTYPE_ANY,
    },
  ],
  configParams: [
    {
      id: 'maxGroupCount',
      name: 'Maximum number of groups',
      description:
        'In case the actual number of states in `compositionGroups` exceeds this, the remaining states will be represented as a single group.',
      groupId: 'columns',
      paramType: createConfigParamTypeRange(5, 30, 1, 10),
    },
    {
      id: 'stackingType',
      name: 'Stacking',
      groupId: 'columns',
      paramType: createConfigParamMultipleChoice([
        { id: 'absolute', name: 'Absolute' },
        { id: 'relative', name: 'Relative' },
      ]),
      description: 'Determines how the stacked bars are displayed along the Y axis.',
    },
    {
      id: 'colorsScheme',
      name: 'Color scheme',
      groupId: 'columns',
      description: 'Discrete color palette used to color code the different bars.',
      paramType: createConfigParamTypeDiscreteColorScheme(),
    },
    {
      id: 'referenceGroupName',
      name: 'Sorting reference group',
      groupId: 'advanced',
      description:
        'Defines a state of `compositionGroups` that is used for sorting the columns in decreasing summed value for that state.',
      paramType: createReferenceGroupParamType,
      visibilityConditions: [
        {
          conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.FACET_UNBOUND,
          conditionInfo: {
            facet: 'sortColumnBy',
          },
        },
      ],
    },
    createCategoricalOrderConfigParam('compositionGroups', 'Composition groups order', 'advanced'),
  ],
  examples: [
    {
      id: 'stackedcomposition_1',
    },
  ],
};

function createReferenceGroupParamType(
  currentConfigParams: TpChartConfigParams,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
) {
  const boundData = getBoundData(manifestStackedCompositionPlot, dataContext, currentDataBinding);
  const groupInfo = boundData.bindings.compositionGroups;

  const uniqueGroupIds = getUniqueValues(groupInfo.data, true);

  const choices: any[] = uniqueGroupIds.map((id) => ({
    id: convertColumnDataToDisplayString(groupInfo.columnInfo, id),
    name: convertColumnDataToDisplayString(groupInfo.columnInfo, id),
  }));

  return {
    configParamTypeId: CONFIGPARAMTYPES.MultipleChoice,
    choices: [
      {
        id: '',
        name: 'Total',
      },
      ...choices,
    ],
    defaultValue: '',
  };
}
